import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Sparkles, MapPin, Radio, RefreshCw, Filter, X, List, CalendarRange, Calendar, Heart, Search, Map, Square, Columns2, Rows3, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { SEO } from '../components/SEO';
import InstagramActivityCard from '../components/InstagramActivityCard';
import ActivityCalendar from '../components/ActivityCalendar';
import ActivityMap from '../components/ActivityMap';
import OnboardingBanner from '../components/home/OnboardingBanner';
import InterestsOnboardingCard from '../components/home/InterestsOnboardingCard';
import DiscoverLinks from '../components/home/DiscoverLinks';
import OrganizeYourselfCTA from '../components/home/OrganizeYourselfCTA';
import FirstTimeOrganizerDialog from '../components/home/FirstTimeOrganizerDialog';
import ScrollToTopButton from '../components/ScrollToTopButton';
import SourceToggle from '../components/SourceToggle';
import { MobileFilters, SidebarFilters } from '../components/home/HomeFilters';
import HomeSidebar from '../components/home/HomeSidebar';
import VerbinderCard from '../components/home/VerbinderCard';
import EmptyStateSuggestions from '../components/home/EmptyStateSuggestions';
import AiSearchBar from '../components/home/AiSearchBar';
import NewsSection from '../components/home/NewsSection';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { ACTIVITY_TYPE_LABELS } from '../lib/constants';
import ColorBlockPlaceholder from '../components/ColorBlockPlaceholder';
import { Badge } from '../components/ui/badge';
import FloatingFilterButton from '../components/FloatingFilterButton';

const CompactActivityRow = ({ activity, t }) => {
  const photoUrl = activity.image_url || activity.pga_photo_url;
  // Use de geoptimaliseerde -thumb.jpg variant voor 64x64 row-thumb (98% kleiner)
  const thumbUrl = (() => {
    if (!photoUrl) return photoUrl;
    if (photoUrl.includes('positiefgezondalmere.nl')) {
      return photoUrl.replace(/-1024x683\.jpg$/, '-768x512.jpg');
    }
    if (photoUrl.startsWith('/images/activity-photos/') && !photoUrl.includes('-thumb')) {
      return photoUrl.replace(/\.(jpe?g|png)$/i, '-thumb.jpg');
    }
    return photoUrl;
  })();
  const typeLabel = ACTIVITY_TYPE_LABELS[activity.activity_type];
  return (
    <Link to={`/activity/${activity.id}`} data-testid={`home-row-${activity.id}`} data-activity-name={activity.title} data-activity-category={activity.activity_type}>
      <Card className="flex items-center gap-4 p-3 hover:shadow-md hover:-translate-y-0.5 transition-all border border-gray-100">
        <div className="w-16 h-16 flex-shrink-0 overflow-hidden">
          {photoUrl ? (
            <img src={thumbUrl} alt={activity.title} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <ColorBlockPlaceholder activityType={activity.activity_type} className="!aspect-square" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-bold text-sm text-gray-900 truncate">{activity.title}</h4>
          <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
            {typeLabel && <span>{typeLabel.icon} {typeLabel.label}</span>}
            <span>{activity.time}</span>
          </div>
        </div>
        {activity.status === 'cancelled' && (
          <Badge className="bg-red-50 text-red-600 border border-red-200 text-xs flex-shrink-0">{t('detail.cancelled')}</Badge>
        )}
      </Card>
    </Link>
  );
};

const Home = ({ preset }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, t, requireAuth } = useAuth();

  // Map URL deeplinks (/jongeren, /senioren, /gratis) to filter overrides.
  // sessionStorage is bypassed for preset routes so the campaign URL always lands clean.
  const presetFilters = React.useMemo(() => {
    const base = { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', is_live: false };
    if (preset === 'jongeren') return { ...base, target_audience: 'Jongeren' };
    if (preset === 'senioren') return { ...base, target_audience: 'Senioren' };
    if (preset === 'volwassenen') return { ...base, target_audience: 'Volwassenen' };
    if (preset === 'kinderen') return { ...base, target_audience: 'Kinderen' };
    // Combined campaign presets (Loes / Apostrof — May 2026)
    if (preset === 'jongeren-volwassenen') return { ...base, target_audience: 'Jongeren,Volwassenen' };
    if (preset === 'volwassenen-senioren') return { ...base, target_audience: 'Volwassenen,Senioren' };
    if (preset === 'gratis') return { ...base, cost_filter: 'free' };
    return null;
  }, [preset]);

  // Lees filter-deeplinks uit de URL-querystring (bv. /?activity_type=sport of /?wijk=Almere%20Buiten).
  // Zo tonen deze URLs echt gefilterde content én een bijpassende (dynamische) H1 (SEO-wens Julian).
  const queryFilters = React.useMemo(() => {
    const base = { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', date_from: '', date_to: '', season: '', is_holiday: false, period: '', is_live: false };
    const at = searchParams.get('activity_type') || '';
    const wk = searchParams.get('wijk') || '';
    const ta = searchParams.get('target_audience') || '';
    const cf = searchParams.get('cost_filter') || '';
    const qp = searchParams.get('q') || '';
    const dy = searchParams.get('day') || '';
    const pod = searchParams.get('part_of_day') || '';
    const dfrom = searchParams.get('date_from') || '';
    const dto = searchParams.get('date_to') || '';
    const seas = searchParams.get('season') || '';
    const hol = searchParams.get('is_holiday') === 'true';
    const per = searchParams.get('period') || '';
    // Ook bij een losse ?q= resetten we de gestructureerde filters (naar de
    // URL-waarden, mogelijk leeg) zodat een deeplink vanuit de Zoekhulp niet
    // vervuild wordt door oude sessionStorage-filters.
    if (!at && !wk && !ta && !cf && !qp && !dy && !pod && !dfrom && !per) return null;
    return { ...base, activity_type: at, wijk: wk, target_audience: ta, cost_filter: cf, day: dy, part_of_day: pod, date_from: dfrom, date_to: dto, season: seas, is_holiday: hol, period: per };
  }, [searchParams]);

  const [activities, setActivities] = useState([]);
  const [forYouActivities, setForYouActivities] = useState([]);
  const [forYouLoading, setForYouLoading] = useState(false);
  const [liveActivities, setLiveActivities] = useState([]);
  const [latestPosts, setLatestPosts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const [wijken, setWijken] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(() => {
    if (presetFilters) return presetFilters;
    if (queryFilters) return queryFilters;
    try {
      const saved = sessionStorage.getItem('hoibuur_home_filters');
      return saved ? JSON.parse(saved) : { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', is_live: false };
    } catch { return { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', is_live: false }; }
  });
  const [pendingFilters, setPendingFilters] = useState(() => {
    if (presetFilters) return presetFilters;
    if (queryFilters) return queryFilters;
    try {
      const saved = sessionStorage.getItem('hoibuur_home_filters');
      return saved ? JSON.parse(saved) : { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', is_live: false };
    } catch { return { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', is_live: false }; }
  });
  const [sourceFilter, setSourceFilter] = useState('all');
  const [pendingSourceFilter, setPendingSourceFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(() => {
    // Behoud zoekterm bij terugkeren vanaf detail-pagina (verzoek Hennie 30 juni 2026).
    // URL-parameter ?q= heeft voorrang zodat deeplinks (bv. /?q=wandel) direct filteren.
    const qParam = searchParams.get('q');
    if (qParam) return qParam;
    try { return sessionStorage.getItem('hoibuur_home_search') || ''; }
    catch { return ''; }
  });
  const [viewMode, setViewMode] = useState(() => {
    if (preset === 'map') return 'map';
    return searchParams.get('view') === 'calendar' ? 'calendar' : 'feed';
  });
  const [layoutMode, setLayoutMode] = useState(() => localStorage.getItem('home_layout') || '1col');

  // Dynamische H1: weerspiegelt de actieve filters/zoekterm zodat elke (gefilterde) staat
  // één zinvolle, beschrijvende H1 heeft (SEO-wens Julian). Valt terug op de standaard-welkomzin.
  const pageHeading = React.useMemo(() => {
    const wijk = (filters.wijk || '').trim();
    const type = (filters.activity_type || '').trim();
    const audience = (filters.target_audience || '').split(',')[0].trim();
    const free = filters.cost_filter === 'free';
    const q = (searchQuery || '').trim();
    const typeLabel = type && ACTIVITY_TYPE_LABELS[type] ? ACTIVITY_TYPE_LABELS[type].label : '';
    const audLabel = audience ? { Kinderen: 'kinderen', Jongeren: 'jongeren', Volwassenen: 'volwassenen', Senioren: 'senioren' }[audience] : '';
    const plaats = wijk || 'Almere';

    if (q) return `Zoeken naar "${q}"${wijk ? ` in ${wijk}` : ' in Almere'}`;
    if (typeLabel) return `${typeLabel} in ${plaats}`;
    if (audLabel) return `Activiteiten voor ${audLabel} in ${plaats}`;
    if (free) return `Gratis activiteiten in ${plaats}`;
    if (wijk) return `Activiteiten in ${wijk}`;
    return t('home.welcome_line');
  }, [filters.wijk, filters.activity_type, filters.target_audience, filters.cost_filter, searchQuery, t]);

  // Synchroniseer filters met URL-deeplinks (bv. vanuit de Zoekhulp of interne links).
  // Zonder dit zou SPA-navigatie de querystring wijzigen zonder de feed/H1 te verversen.
  useEffect(() => {
    const next = presetFilters || queryFilters;
    if (next) {
      setFilters(next);
      setPendingFilters(next);
    }
    const q = searchParams.get('q');
    if (q !== null) setSearchQuery(q);
    else if (next) setSearchQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetFilters, queryFilters, searchParams]);

  // "Meer opties" collapse state — toont/verbergt filters, zoek, view-switcher.
  // Default: open op mobiel (zoeken en filteren direct zichtbaar — verzoek Kirsten 15 mei).
  // Op desktop staat de sidebar al altijd open, dus deze state heeft daar geen effect.
  // Gebruiker kan inklappen; keuze wordt onthouden in localStorage.
  const [optionsOpen, setOptionsOpen] = useState(() => {
    const saved = localStorage.getItem('home_options_open');
    if (saved === '1') return true;
    if (saved === '0') return false;
    return true; // nieuwe default: open
  });
  const toggleOptions = () => {
    const next = !optionsOpen;
    setOptionsOpen(next);
    localStorage.setItem('home_options_open', next ? '1' : '0');
  };
  const [showStoryDialog, setShowStoryDialog] = useState(false);
  const [storyForm, setStoryForm] = useState({ name: '', email: '', phone: '', description: '' });
  const [storySubmitting, setStorySubmitting] = useState(false);
  const limit = 10;

  const hasActiveFilters = pendingFilters.wijk || pendingFilters.activity_type || pendingFilters.date_filter || pendingFilters.date_from || pendingFilters.period || (pendingFilters.age_group && pendingFilters.age_group !== 'all') || (pendingSourceFilter && pendingSourceFilter !== 'all') || pendingFilters.cost_filter || (pendingFilters.target_audience && pendingFilters.target_audience !== 'all') || (searchQuery && searchQuery.trim().length >= 2);

  const activeFilterCount = [
    pendingFilters.wijk,
    pendingFilters.activity_type,
    pendingFilters.date_filter,
    pendingFilters.cost_filter,
    pendingFilters.age_group && pendingFilters.age_group !== 'all' ? pendingFilters.age_group : null,
    pendingFilters.target_audience && pendingFilters.target_audience !== 'all' ? pendingFilters.target_audience : null,
    pendingSourceFilter && pendingSourceFilter !== 'all' ? pendingSourceFilter : null,
    searchQuery && searchQuery.trim().length >= 2 ? searchQuery : null,
  ].filter(Boolean).length;

  // Scroll restoration: save position before leaving, restore after activities load
  const pendingScroll = useRef(null);

  useEffect(() => {
    const savedPos = sessionStorage.getItem('home_scroll');
    if (savedPos) {
      pendingScroll.current = parseInt(savedPos, 10);
      sessionStorage.removeItem('home_scroll');
    }
  }, []);

  useEffect(() => {
    if (pendingScroll.current && !loading && activities.length > 0) {
      const pos = pendingScroll.current;
      pendingScroll.current = null;
      setTimeout(() => window.scrollTo(0, pos), 50);
    }
  }, [loading, activities.length]);

  useEffect(() => {
    const saveScroll = () => sessionStorage.setItem('home_scroll', window.scrollY.toString());
    const links = document.querySelectorAll('a[href^="/activity/"]');
    links.forEach(link => link.addEventListener('click', saveScroll));
    return () => links.forEach(link => link.removeEventListener('click', saveScroll));
  });

  // IntersectionObserver auto-load bewust uitgeschakeld — gebruikers moeten expliciet op "Meer activiteiten bekijken" klikken.

  useEffect(() => {
    fetchReferenceData();
    fetchData();
    const interval = setInterval(fetchLiveActivities, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    setActivities([]);
    setSkip(0);
    setHasMore(true);
    setLoading(true);
    fetchActivitiesWithFilter();
  }, [filters, sourceFilter]);

  useEffect(() => {
    if (viewMode === 'foryou' && user?.interests?.length > 0) fetchForYou();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, user]);

  // Debounced search + persist searchQuery zodat 'terug' uit detail-pagina de zoekterm behoudt
  useEffect(() => {
    try {
      if (searchQuery && searchQuery.trim().length > 0) {
        sessionStorage.setItem('hoibuur_home_search', searchQuery);
      } else {
        sessionStorage.removeItem('hoibuur_home_search');
      }
    } catch { /* sessionStorage unavailable */ }
    const timer = setTimeout(() => {
      setActivities([]);
      setSkip(0);
      setHasMore(true);
      setLoading(true);
      fetchActivitiesWithFilter();
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchReferenceData = async () => {
    try {
      const [wijkenRes, typesRes] = await Promise.all([api.get(`/wijken`), api.get(`/activity-types`)]);
      setWijken(wijkenRes.data.wijken);
      setActivityTypes(typesRes.data.types);
    } catch (error) { console.error('Failed to fetch reference data:', error); }
  };

  const fetchData = async () => {
    try {
      const [liveRes, postsRes, testimonialsRes] = await Promise.all([
        api.get(`/activities/live`),
        api.get(`/posts/latest?limit=3`),
        api.get(`/testimonials?limit=4`).catch(() => ({ data: [] }))
      ]);
      setLiveActivities(liveRes.data);
      setLatestPosts(postsRes.data);
      // Shuffle testimonials so the same age cluster doesn't always lead.
      const tList = [...(testimonialsRes.data || [])];
      for (let i = tList.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [tList[i], tList[j]] = [tList[j], tList[i]];
      }
      setTestimonials(tList);
    } catch (error) { console.error('Failed to fetch data:', error); }
  };

  const buildParams = (extraSkip) => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (extraSkip) params.append('skip', extraSkip.toString());
    // #3 dagelijkse rotatie + #4 doelgroep-primary-first (backend re-rank)
    params.append('rotate_daily', 'true');
    if (filters.wijk) params.append('wijk', filters.wijk);
    if (filters.activity_type) params.append('activity_type', filters.activity_type);
    if (filters.date_filter) params.append('date_filter', filters.date_filter);
    if (filters.age_group && filters.age_group !== 'all') params.append('age_group', filters.age_group);
    if (filters.target_audience && filters.target_audience !== 'all') params.append('target_audience', filters.target_audience);
    if (filters.cost_filter && filters.cost_filter !== 'all') params.append('cost_filter', filters.cost_filter);
    if (filters.day) params.append('day', filters.day);
    if (filters.part_of_day) params.append('part_of_day', filters.part_of_day);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.is_holiday) params.append('is_holiday', 'true');
    if (filters.season) params.append('season', filters.season);
    if (filters.is_live) params.append('is_live', 'true');
    if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
    return params.toString();
  };

  const fetchActivitiesWithFilter = async () => {
    try {
      let response;
      if (searchQuery && searchQuery.trim().length >= 2) {
        const params = new URLSearchParams();
        params.append('q', searchQuery.trim());
        // Volg de bestaande filterlogica: de zoek-endpoint ondersteunt wijk en
        // activity_type, dus sturen we die mee i.p.v. ze te laten vallen.
        if (filters.wijk) params.append('wijk', filters.wijk);
        if (filters.activity_type) params.append('activity_type', filters.activity_type);
        if (filters.target_audience && filters.target_audience !== 'all') params.append('target_audience', filters.target_audience);
        if (filters.day) params.append('day', filters.day);
        if (filters.part_of_day) params.append('part_of_day', filters.part_of_day);
        if (filters.date_from) params.append('date_from', filters.date_from);
        if (filters.date_to) params.append('date_to', filters.date_to);
        if (filters.is_holiday) params.append('is_holiday', 'true');
        if (filters.season) params.append('season', filters.season);
        if (sourceFilter === 'pga') params.append('source', 'pga');
        response = await api.get(`/activities/search?${params.toString()}`);
      } else {
        response = await api.get(`/activities?${buildParams()}`);
      }
      setActivities(response.data);
      setHasMore(!searchQuery && response.data.length === limit);
      setSkip(limit);
    } catch (error) { console.error('Failed to fetch activities:', error); }
    finally { setLoading(false); }
  };

  const fetchLiveActivities = async () => {
    try { const r = await api.get(`/activities/live`); setLiveActivities(r.data); } catch (e) {}
  };

  const fetchForYou = async () => {
    setForYouLoading(true);
    try {
      const res = await api.get('/activities/for-you?limit=20');
      setForYouActivities(res.data);
    } catch (error) { console.error('Failed to fetch for-you:', error); }
    finally { setForYouLoading(false); }
  };

  const loadMoreActivities = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const response = await api.get(`/activities?${buildParams(skip)}`);
      setActivities(prev => [...prev, ...response.data]);
      setHasMore(response.data.length === limit);
      setSkip(prev => prev + limit);
    } catch (error) { console.error('Failed to load more:', error); }
    finally { setLoadingMore(false); }
  };

  const handleFilterChange = (key, value) => {
    setPendingFilters(prev => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const applyFilters = () => {
    setFilters({ ...pendingFilters });
    setSourceFilter(pendingSourceFilter);
    try { sessionStorage.setItem('hoibuur_home_filters', JSON.stringify(pendingFilters)); } catch {}
  };

  const clearFilters = () => {
    const empty = { wijk: '', activity_type: '', date_filter: '', age_group: '', target_audience: '', cost_filter: '', day: '', part_of_day: '', is_live: false };
    setPendingFilters(empty);
    setPendingSourceFilter('all');
    setFilters(empty);
    setSourceFilter('all');
    setSearchQuery('');
    try { sessionStorage.removeItem('hoibuur_home_filters'); } catch {}
  };

  const handleShareStory = () => {
    if (requireAuth('Log in om een verhaal te delen.')) {
      setShowStoryDialog(true);
    }
  };

  const submitStoryRequest = async () => {
    if (!storyForm.name.trim() || !storyForm.description.trim()) {
      toast.error('Vul je naam en een korte beschrijving in');
      return;
    }
    setStorySubmitting(true);
    try {
      await api.post('/story-requests', storyForm);
      toast.success('Bedankt! We nemen contact met je op.');
      setShowStoryDialog(false);
      setStoryForm({ name: '', email: '', phone: '', description: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kon verzoek niet versturen');
    } finally {
      setStorySubmitting(false);
    }
  };

  const filterProps = { pendingFilters, wijken, activityTypes, hasActiveFilters, onPendingFilterChange: handleFilterChange, onApply: applyFilters, clearFilters, t, pendingSourceFilter, onPendingSourceFilterChange: setPendingSourceFilter };

  return (
    <div className="space-y-6" data-testid="home-page">
      <SEO title="Home" description="Ontdek lokale activiteiten en ontmoet je buren in Almere. Sport, cultuur, natuur en meer - altijd dichtbij." path="/" />

      {!user && <OnboardingBanner user={user} t={t} />}
      {user && <FirstTimeOrganizerDialog />}

      {/* Doelgroep-preset banner (campaign deeplinks /jongeren, /senioren, etc.)
          — rendered for BOTH logged-in and anonymous visitors so campaign traffic
          immediately understands they landed on a filtered view.
          De 'map' preset is puur een view-mode deeplink, dus geen banner. */}
      {preset && preset !== 'map' && (
        <div className="bg-hoi-blue/10 border-2 border-hoi-blue/20 px-4 py-3 flex items-center justify-between gap-3" data-testid="preset-banner">
          <p className="text-sm text-hoi-blue font-medium">
            {preset === 'jongeren' && 'Speciaal voor jongeren — '}
            {preset === 'senioren' && 'Speciaal voor senioren — '}
            {preset === 'volwassenen' && 'Speciaal voor volwassenen — '}
            {preset === 'kinderen' && 'Speciaal voor kinderen — '}
            {preset === 'gratis' && 'Alleen gratis activiteiten — '}
            we tonen alvast de juiste activiteiten.
          </p>
          <button onClick={() => navigate('/')} className="text-xs text-hoi-blue hover:underline whitespace-nowrap" data-testid="preset-clear">
            Toon alles →
          </button>
        </div>
      )}

      {/* Activity Feed — visible for BOTH logged-in and non-logged-in users */}
      {user ? (
        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-8">
          {/* Main Column */}
          <div className="space-y-6">
            <h1 className="sr-only">{pageHeading}</h1>
            {/* Welcome Card */}
            <section className="animate-fade-in-up">
              <Card className="bg-hoi-blue text-white p-4 md:p-6 rounded-3xl border-0 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-xl">{t('home.welcome')}</h2>
                    <p className="opacity-90 text-sm"><MapPin className="inline w-3 h-3 mr-1" />{user.wijk}</p>
                  </div>
                </div>
              </Card>
            </section>

            <InterestsOnboardingCard onSaved={() => setViewMode('foryou')} />

            <MobileFilters {...filterProps} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />

            {/* Verbinder van de week — mobiel (desktop toont 'm in de zijbalk) */}
            <div className="lg:hidden" data-testid="verbinder-mobile">
              <VerbinderCard />
            </div>

            {/* Feed Header */}
            <section className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <h2 className="font-heading font-bold text-2xl text-primary">
                  {viewMode === 'feed' ? t('home.feed') : viewMode === 'foryou' ? t('foryou.title') : viewMode === 'calendar' ? t('home.calendar') : t('discover.tab_map')}
                  {viewMode === 'feed' && hasActiveFilters && (
                    <span className="text-sm font-normal text-muted-foreground ml-2">({activities.length} resultaten)</span>
                  )}
                </h2>
                <Link to="/activiteit-toevoegen" data-testid="home-add-activity-btn" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto rounded-full bg-hoi-coral hover:bg-hoi-coral/90 text-white font-heading whitespace-nowrap px-5 py-3 shadow-md shadow-hoi-coral/25 active:scale-95 transition-transform">
                    <Plus className="w-4 h-4 mr-1.5" aria-hidden="true" />
                    {t('nav.create')}
                  </Button>
                </Link>
              </div>

              {/* AI-gedreven zoekbalk (ingelogd) */}
              {viewMode === 'feed' && <AiSearchBar />}

              {/* Unified view & layout switcher */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2" role="group" aria-label="Weergave opties">
                <div className="flex items-center gap-1" role="tablist" aria-label="Weergave type">
                  {[
                    ...(user?.interests?.length > 0 ? [{ id: 'foryou', icon: Heart, label: t('foryou.tab'), testId: 'map-toggle-foryou' }] : []),
                    { id: 'feed', icon: List, label: 'Lijst', testId: 'map-toggle-feed' },
                    { id: 'calendar', icon: CalendarRange, label: 'Agenda', testId: 'map-toggle-calendar' },
                    { id: 'map', icon: Map, label: 'Kaart', testId: 'map-toggle-btn' },
                  ].map(({ id, icon: Icon, label, testId }) => (
                    <button key={id} onClick={() => setViewMode(id)}
                      className={`inline-flex items-center justify-center gap-1.5 px-3 min-h-[44px] text-sm font-medium transition-colors
                        ${viewMode === id
                          ? 'bg-hoi-blue text-white shadow-sm'
                          : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700'}`}
                      role="tab" aria-selected={viewMode === id} aria-label={label} title={label} data-testid={testId}>
                      <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                      {id === 'foryou' && <span className="text-xs font-semibold">{label}</span>}
                    </button>
                  ))}
                </div>
                {viewMode === 'feed' && (
                  <div className="flex items-center gap-1" role="tablist" aria-label="Kaartgrootte">
                    {[
                      { mode: '1col', icon: Square, label: 'Groot', hide: '' },
                      { mode: '2col', icon: Columns2, label: 'Klein', hide: 'hidden sm:inline-flex' },
                      { mode: 'list', icon: Rows3, label: 'Compact', hide: '' },
                    ].map(({ mode, icon: Icon, label, hide }) => (
                      <button key={mode}
                        onClick={() => { setLayoutMode(mode); localStorage.setItem('home_layout', mode); }}
                        className={`${hide || 'inline-flex'} items-center justify-center gap-1.5 px-3 min-h-[44px] text-sm font-medium transition-colors
                          ${layoutMode === mode
                            ? 'bg-hoi-blue text-white shadow-sm'
                            : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700'}`}
                        role="tab" aria-selected={layoutMode === mode} aria-label={label} title={label} data-testid={`home-layout-${mode}`}>
                        <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {viewMode === 'calendar' && (
                <Card className="p-4 md:p-6 bg-white border border-primary/5 shadow-soft">
                  <ActivityCalendar />
                </Card>
              )}

              {viewMode === 'map' && (
                <ActivityMap />
              )}

              {viewMode === 'foryou' && (
                <div data-testid="foryou-section">
                  <p className="text-sm text-muted-foreground mb-4">{t('foryou.subtitle')}</p>
                  {forYouLoading ? (
                    <div className="space-y-8 max-w-lg mx-auto lg:max-w-none">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="overflow-hidden rounded-3xl">
                          <div className="p-4 flex items-center gap-3"><Skeleton className="w-10 h-10 rounded-full" /><div className="space-y-2"><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-16" /></div></div>
                          <Skeleton className="w-full aspect-square" />
                          <div className="p-4 space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
                        </Card>
                      ))}
                    </div>
                  ) : forYouActivities.length > 0 ? (
                    <div className="space-y-8 max-w-lg mx-auto lg:max-w-none">
                      {forYouActivities.map((activity) => (
                        <InstagramActivityCard key={activity.id} activity={activity} />
                      ))}
                    </div>
                  ) : (
                    <Card className="p-12 text-center rounded-3xl bg-white border border-primary/5 max-w-lg mx-auto lg:max-w-none">
                      <Heart className="w-16 h-16 mx-auto mb-4 text-muted" aria-hidden="true" />
                      <p className="text-muted-foreground mb-6">{t('foryou.empty')}</p>
                      <Link to="/settings"><Button variant="outline" className="rounded-full" data-testid="foryou-edit-interests">{t('nav.profile')}</Button></Link>
                    </Card>
                  )}
                </div>
              )}

              {viewMode === 'feed' && (
                <>

                  {loading ? (
                    <div className="space-y-8 max-w-lg mx-auto lg:max-w-none">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="overflow-hidden rounded-3xl">
                          <div className="p-4 flex items-center gap-3"><Skeleton className="w-10 h-10 rounded-full" /><div className="space-y-2"><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-16" /></div></div>
                          <Skeleton className="w-full aspect-square" />
                          <div className="p-4 space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
                        </Card>
                      ))}
                    </div>
                  ) : activities.length > 0 ? (
                    <>
                      {layoutMode === 'list' ? (
                        <div className="space-y-2">
                          {activities.map((activity, idx) => (
                            <React.Fragment key={activity.id}>
                              <CompactActivityRow activity={activity} t={t} />
                              {idx === 2 && <OrganizeYourselfCTA />}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <div className={layoutMode === '2col' ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'space-y-8 max-w-lg mx-auto lg:max-w-none'}>
                          {activities.map((activity, idx) => (
                            <React.Fragment key={activity.id}>
                              <InstagramActivityCard activity={activity} />
                              {idx === 2 && (
                                <div className={layoutMode === '2col' ? 'md:col-span-2' : ''}>
                                  <OrganizeYourselfCTA />
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                      {loadingMore && (
                        <div className="flex justify-center py-4"><RefreshCw className="w-6 h-6 text-brand animate-spin" /></div>
                      )}
                      {hasMore && !loadingMore && (
                        <div className="text-center py-4">
                          <Button
                            variant="outline"
                            onClick={loadMoreActivities}
                            className="px-8 py-3 rounded-full font-heading font-bold border-2 border-hoi-blue text-hoi-blue hover:bg-hoi-blue/5"
                            data-testid="feed-more-btn"
                          >
                            {t('detail.more_activities')}
                          </Button>
                        </div>
                      )}
                      {!hasMore && activities.length > 0 && (
                        <p className="text-center text-muted-foreground py-4" aria-live="polite">Je hebt alles gezien!</p>
                      )}
                      {activities.length >= 3 && hasActiveFilters && !hasMore && (
                        <EmptyStateSuggestions
                          filters={filters}
                          onClearFilters={clearFilters}
                          variant="append"
                          excludeIds={activities.map(a => a.id)}
                        />
                      )}
                    </>
                  ) : (
                    <Card className="p-12 text-center rounded-3xl bg-white border border-primary/5 max-w-lg mx-auto lg:max-w-none">
                      {filters.is_live ? (
                        <><Radio className="w-16 h-16 mx-auto mb-4 text-muted" aria-hidden="true" /><h3 className="font-heading font-bold text-xl text-primary mb-2">Geen live activiteiten</h3><p className="text-muted-foreground mb-6">Er zijn momenteel geen live activiteiten.</p></>
                      ) : hasActiveFilters ? (
                        <EmptyStateSuggestions
                          filters={filters}
                          onClearFilters={clearFilters}
                          variant="empty"
                        />
                      ) : (
                        <><Calendar className="w-16 h-16 mx-auto mb-4 text-muted" aria-hidden="true" /><h3 className="font-heading font-bold text-xl text-primary mb-2">Nog geen activiteiten</h3><p className="text-muted-foreground mb-6">Wees de eerste om een activiteit te starten in jouw buurt!</p><Link to="/activiteit-toevoegen"><Button className="btn-primary">Maak een activiteit</Button></Link></>
                      )}
                    </Card>
                  )}
                </>
              )}
            </section>
          </div>

          {/* Sidebar (Desktop) */}
          <aside className="hidden lg:block space-y-6" aria-label="Zijbalk">
            <SidebarFilters {...filterProps} />
            <HomeSidebar latestPosts={latestPosts} testimonials={testimonials} t={t} onShareStory={handleShareStory} />
          </aside>
        </div>
      ) : (
        /* Non-logged-in: same layout as logged-in with sidebar */
        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-8">
          <div className="space-y-4">
            {/* Welkomstregel + Action Row (Voeg toe + Meer opties) */}
            <div id="activities-feed" className="space-y-3" data-testid="landing-welcome-section">
              <h1
                className="font-heading font-bold text-xl sm:text-2xl text-primary leading-snug"
                data-testid="welcome-heading"
              >
                {pageHeading}
              </h1>
              <div className="flex items-center gap-2">
                <Link to="/activiteit-toevoegen" data-testid="landing-add-activity-btn" className="flex-1 sm:flex-initial">
                  <Button
                    size="sm"
                    className="w-full sm:w-auto rounded-full bg-hoi-coral hover:bg-hoi-coral/90 text-white font-heading whitespace-nowrap px-4 py-2.5 shadow-md shadow-hoi-coral/25 active:scale-95 transition-transform"
                  >
                    <Plus className="w-4 h-4 mr-1.5" aria-hidden="true" />
                    {t('nav.create')}
                  </Button>
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleOptions}
                  aria-expanded={optionsOpen}
                  aria-controls="landing-options-collapse"
                  data-testid="meer-opties-toggle"
                  className="flex-1 sm:flex-initial rounded-full border-2 border-gray-200 text-gray-700 font-heading whitespace-nowrap px-4 py-2.5 hover:bg-gray-50 lg:hidden"
                >
                  {optionsOpen ? <ChevronUp className="w-4 h-4 mr-1.5" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 mr-1.5" aria-hidden="true" />}
                  {optionsOpen ? t('home.less_options') : t('home.more_options')}
                </Button>
              </div>
            </div>

            {/* Collapsible options: filters + zoek + view-switcher.
                Default collapsed op mobiel; op desktop (lg:) altijd zichtbaar. */}
            <div
              id="landing-options-collapse"
              data-testid="options-collapse-area"
              className={`${optionsOpen ? 'block' : 'hidden'} lg:block space-y-3 animate-in fade-in slide-in-from-top-2 duration-200`}
            >
              <MobileFilters {...filterProps} filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />

              {/* AI-gedreven zoekbalk (niet-ingelogd) */}
              {viewMode === 'feed' && <AiSearchBar />}

              {/* Unified view & layout switcher (non-logged-in) */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2" role="group" aria-label="Weergave opties">
                <div className="flex items-center gap-1" role="tablist" aria-label="Weergave type" data-testid="landing-view-toggle">
                  {[
                    { id: 'feed', icon: List, label: 'Lijst', testId: 'landing-feed-btn' },
                    { id: 'calendar', icon: CalendarRange, label: 'Agenda', testId: 'landing-agenda-btn' },
                    { id: 'map', icon: Map, label: 'Kaart', testId: 'landing-map-btn' },
                  ].map(({ id, icon: Icon, label, testId }) => (
                    <button key={id} onClick={() => setViewMode(id)}
                      className={`inline-flex items-center justify-center gap-1.5 px-3 min-h-[44px] text-sm font-medium transition-colors
                        ${viewMode === id
                          ? 'bg-hoi-blue text-white shadow-sm'
                          : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700'}`}
                      role="tab" aria-selected={viewMode === id} aria-label={label} title={label} data-testid={testId}>
                      <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                    </button>
                  ))}
                </div>
                {viewMode === 'feed' && (
                  <div className="flex items-center gap-1" role="tablist" aria-label="Kaartgrootte">
                    {[
                      { mode: '1col', icon: Square, label: 'Groot', hide: '' },
                      { mode: '2col', icon: Columns2, label: 'Klein', hide: 'hidden sm:inline-flex' },
                      { mode: 'list', icon: Rows3, label: 'Compact', hide: '' },
                    ].map(({ mode, icon: Icon, label, hide }) => (
                      <button key={mode}
                        onClick={() => { setLayoutMode(mode); localStorage.setItem('home_layout', mode); }}
                        className={`${hide || 'inline-flex'} items-center justify-center gap-1.5 px-3 min-h-[44px] text-sm font-medium transition-colors
                          ${layoutMode === mode
                            ? 'bg-hoi-blue text-white shadow-sm'
                            : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700'}`}
                        role="tab" aria-selected={layoutMode === mode} aria-label={label} title={label} data-testid={`landing-layout-${mode}`}>
                        <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Verbinder van de week — mobiel (desktop toont 'm in de zijbalk) */}
            <div className="lg:hidden" data-testid="verbinder-mobile-landing">
              <VerbinderCard />
            </div>

            {viewMode === 'calendar' && (
              <Card className="p-4 md:p-6 bg-white border border-primary/5 shadow-soft">
                <ActivityCalendar />
              </Card>
            )}

            {viewMode === 'map' && (
              <ActivityMap />
            )}

            {viewMode === 'feed' && (
              <>
                <h2 className="sr-only">Activiteiten in Almere</h2>

                {loading ? (
                  <div className="space-y-8 max-w-lg mx-auto lg:max-w-none">
                    {[1, 2, 3].map((i) => (
                      <Card key={i} className="overflow-hidden rounded-3xl">
                        <div className="p-4 flex items-center gap-3"><Skeleton className="w-10 h-10 rounded-full" /><div className="space-y-2"><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-16" /></div></div>
                        <Skeleton className="w-full aspect-square" />
                        <div className="p-4 space-y-3"><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-2/3" /></div>
                      </Card>
                    ))}
                  </div>
                ) : activities.length > 0 ? (
                  <>
                    {layoutMode === 'list' ? (
                      <div className="space-y-2">
                        {activities.map((activity, idx) => (
                          <React.Fragment key={activity.id}>
                            <CompactActivityRow activity={activity} t={t} />
                            {idx === 2 && <OrganizeYourselfCTA />}
                          </React.Fragment>
                        ))}
                      </div>
                    ) : (
                      <div className={layoutMode === '2col' ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'space-y-8 max-w-lg mx-auto lg:max-w-none'}>
                        {activities.map((activity, idx) => (
                          <React.Fragment key={activity.id}>
                            <InstagramActivityCard activity={activity} />
                            {idx === 2 && (
                              <div className={layoutMode === '2col' ? 'md:col-span-2' : ''}>
                                <OrganizeYourselfCTA />
                              </div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    {loadingMore && (
                      <div className="flex justify-center py-4"><RefreshCw className="w-6 h-6 text-brand animate-spin" /></div>
                    )}
                    {hasMore && !loadingMore && (
                      <div className="text-center py-4">
                        <Button
                          variant="outline"
                          onClick={loadMoreActivities}
                          className="px-8 py-3 rounded-full font-heading font-bold border-2 border-hoi-blue text-hoi-blue hover:bg-hoi-blue/5"
                          data-testid="feed-more-btn"
                        >
                          {t('detail.more_activities')}
                        </Button>
                      </div>
                    )}
                    {!hasMore && activities.length > 0 && (
                      <p className="text-center text-muted-foreground py-4" aria-live="polite">Je hebt alles gezien!</p>
                    )}
                    {activities.length >= 3 && hasActiveFilters && !hasMore && (
                      <EmptyStateSuggestions
                        filters={filters}
                        onClearFilters={clearFilters}
                        variant="append"
                        excludeIds={activities.map(a => a.id)}
                      />
                    )}
                  </>
                ) : (
                  <Card className="p-12 text-center rounded-3xl bg-white border border-primary/5 max-w-lg mx-auto lg:max-w-none">
                    {filters.is_live ? (
                      <><Radio className="w-16 h-16 mx-auto mb-4 text-muted" aria-hidden="true" /><h3 className="font-heading font-bold text-xl text-primary mb-2">Geen live activiteiten</h3><p className="text-muted-foreground mb-6">Er zijn momenteel geen live activiteiten.</p></>
                    ) : hasActiveFilters ? (
                      <EmptyStateSuggestions
                        filters={filters}
                        onClearFilters={clearFilters}
                        variant="empty"
                      />
                    ) : (
                      <><Calendar className="w-16 h-16 mx-auto mb-4 text-muted" aria-hidden="true" /><h3 className="font-heading font-bold text-xl text-primary mb-2">Binnenkort meer activiteiten</h3><p className="text-muted-foreground mb-6">Meld je aan om als eerste te horen wanneer er iets te doen is.</p><Link to="/register"><Button className="btn-primary">{t('home.register_cta')}</Button></Link></>
                    )}
                  </Card>
                )}
              </>
            )}
          </div>

          {/* Sidebar (Desktop) - same as logged-in */}
          <aside className="hidden lg:block space-y-6" aria-label="Zijbalk">
            <SidebarFilters {...filterProps} />
            <HomeSidebar latestPosts={latestPosts} testimonials={testimonials} t={t} onShareStory={handleShareStory} />
          </aside>
        </div>
      )}

      {/* Landing content verplaatst naar /welkom — bezoekers komen daar via
          de OnboardingBanner of de footer-link. */}

      {/* Prominente interne-link-sectie (SEO + ontdekken) — vervangt de
          bijna-onzichtbare footer-links als primaire ingang. */}
      <DiscoverLinks />

      <NewsSection latestPosts={latestPosts} testimonials={testimonials} t={t} user={user} />

      {/* Share Story Dialog (accessible from landing page) */}
      <Dialog open={showStoryDialog} onOpenChange={setShowStoryDialog}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Heart className="w-5 h-5 text-hoi-coral" />Deel jouw verhaal
            </DialogTitle>
            <DialogDescription>
              Heb jij ook een leuke ervaring met een activiteit in Almere? We maken graag een afspraak om jouw verhaal te horen!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Je naam</label>
              <Input value={storyForm.name} onChange={(e) => setStoryForm({ ...storyForm, name: e.target.value })} placeholder="Hoe heet je?" className="rounded-xl" data-testid="home-story-name" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">E-mail of telefoonnummer</label>
              <Input value={storyForm.email} onChange={(e) => setStoryForm({ ...storyForm, email: e.target.value })} placeholder="Zodat we je kunnen bereiken" className="rounded-xl" data-testid="home-story-email" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Vertel kort over jouw ervaring</label>
              <Textarea value={storyForm.description} onChange={(e) => setStoryForm({ ...storyForm, description: e.target.value })} placeholder="Wat heb je meegemaakt? Bij welke activiteit?" className="rounded-xl min-h-[100px]" data-testid="home-story-desc" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowStoryDialog(false)} className="rounded-full">Annuleren</Button>
            <Button onClick={submitStoryRequest} disabled={storySubmitting} className="rounded-full bg-hoi-coral hover:bg-hoi-coral/90 text-black font-heading font-semibold" data-testid="home-story-submit">
              {storySubmitting ? 'Versturen...' : 'Verstuur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating filter button for mobile */}
      {viewMode === 'feed' && (
        <FloatingFilterButton
          onClick={() => setFiltersOpen(true)}
          hasActiveFilters={hasActiveFilters}
          activeCount={activeFilterCount}
        />
      )}

      {/* Floating "terug naar boven" knop — verschijnt na 600px scroll */}
      <ScrollToTopButton />

      {/* Zoekhulp verwijderd — AI zit nu zichtbaar in de hoofdzoekbalk (AiSearchBar) */}
    </div>
  );
};

export default Home;
